/**
 * Direct browser → Anthropic provider (SECURITY §3, P-3): a thin `fetch` client for `POST /v1/messages` with streaming.
 * The customer's key goes in `x-api-key` with `anthropic-dangerous-direct-browser-access: true` (Anthropic allows browser CORS
 * on this path). It handles `stop_reason: max_tokens` (a larger retry), 429/529/5xx and network errors (`retry-after`,
 * exponential backoff with jitter), the model allowlist, and rebuilds every error as `{status, type, message}`.
 * The key is never logged and never appears in an error.
 */
import type {
	CompletionRequest,
	CompletionResponse,
	CompletionUsage,
	Provider,
} from '../../engine/bench/types';
import { isAllowedModel } from '../../shared/models';
import { ProviderError, errorFrom, scrub } from './errors';
import { MAX_TOKENS_CEILING_V1, buildMessageParams, parsedOf } from './request';
import { SseParser } from './sse';

export const API_URL = 'https://api.anthropic.com/v1/messages';
export const API_VERSION = '2023-06-01';
/** Largest `max_tokens` a v1 truncation retry asks for. */
export const MAX_TOKENS_CEILING = MAX_TOKENS_CEILING_V1;

export interface AnthropicOptions {
	apiKey: string;
	fetch?: typeof fetch;
	url?: string;
	signal?: AbortSignal;
	/** Retries after a retryable failure (default 4: five attempts in all). */
	maxRetries?: number;
	/**
	 * Retries of a truncated (`max_tokens`) output with a larger limit (default 2). Applies to requests without a `schema` (the
	 * v1 loop); a v2 request is retried once at 64,000 by the loop itself (BENCH-V2-SPEC §6.2), so the provider returns it.
	 */
	maxTruncationRetries?: number;
	baseDelayMs?: number;
	maxDelayMs?: number;
	/** Test seams. */
	sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
	random?: () => number;
	now?: () => number;
	/** Cache the system prompt when it meets the model's minimum (default true). */
	cache?: boolean;
	/**
	 * Called each time a 429 or 529 is received and the provider is about to retry it, with the server's `retry-after` in
	 * milliseconds (null when absent). The bench limiter's `noteRateLimited` (BENCH-V3-PLAN §3.3, ED-86). The final failure,
	 * which is thrown, is not reported here: the caller sees it as the rejection.
	 */
	onRateLimited?: (retryAfterMs: number | null) => void;
	/**
	 * Called on every response with the `anthropic-ratelimit-output-tokens-remaining` / `-reset` header values (null when
	 * absent or unreadable; a browser may not expose them through CORS). The bench limiter's `noteBudget` (§3.3).
	 */
	onRateLimitHeaders?: (h: {
		outputTokensRemaining: number | null;
		outputTokensReset: string | null;
	}) => void;
}

const RETRYABLE = new Set([408, 409, 429, 500, 502, 503, 504, 529]);

const defaultSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted)
			return reject(new ProviderError({ status: 0, type: 'cancelled', message: 'cancelled' }));
		const t = setTimeout(() => {
			signal?.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		const onAbort = () => {
			clearTimeout(t);
			reject(new ProviderError({ status: 0, type: 'cancelled', message: 'cancelled' }));
		};
		signal?.addEventListener('abort', onAbort, { once: true });
	});

/** `retry-after` as seconds or an HTTP date, in milliseconds; null when absent or unreadable. */
export function retryAfterMs(header: string | null, now: number): number | null {
	if (header === null) return null;
	const t = header.trim();
	if (/^\d+(?:\.\d+)?$/.test(t)) return Math.round(Number(t) * 1000);
	const date = Date.parse(t);
	return Number.isNaN(date) ? null : Math.max(0, date - now);
}

/** Backoff for the n-th retry (0-based): the server's `retry-after` when given, else 2^n × base with full jitter, capped. */
export function backoffMs(
	attempt: number,
	retryAfter: number | null,
	o: { base: number; max: number; random: () => number }
): number {
	if (retryAfter !== null) return Math.min(retryAfter, o.max);
	const ceiling = Math.min(o.max, o.base * 2 ** attempt);
	return Math.round(ceiling / 2 + (o.random() * ceiling) / 2);
}

/** Calls a caller's hook; a hook that throws must never break or alter a request. */
function safely<A extends unknown[]>(fn: ((...a: A) => void) | undefined, ...args: A): void {
	if (!fn) return;
	try {
		fn(...args);
	} catch {
		// the hook's failure is the hook's own
	}
}

function addUsage(a: CompletionUsage | undefined, b: CompletionUsage): CompletionUsage {
	if (!a) return { ...b };
	return {
		inputTokens: a.inputTokens + b.inputTokens,
		outputTokens: a.outputTokens + b.outputTokens,
		cacheReadTokens: (a.cacheReadTokens ?? 0) + (b.cacheReadTokens ?? 0),
		cacheWriteTokens: (a.cacheWriteTokens ?? 0) + (b.cacheWriteTokens ?? 0),
	};
}

interface StreamResult {
	text: string;
	stopReason: string;
	usage: CompletionUsage;
}

/** Reads a Messages stream to the end. Throws `ProviderError` for an `error` event (with the status Anthropic implies). */
export async function readMessageStream(
	body: ReadableStream<Uint8Array>,
	secrets: readonly string[]
): Promise<StreamResult> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	const parser = new SseParser();
	let text = '';
	let stopReason = '';
	const usage: CompletionUsage = {
		inputTokens: 0,
		outputTokens: 0,
		cacheReadTokens: 0,
		cacheWriteTokens: 0,
	};
	let ended = false;

	const handle = (event: { event: string; data: string }) => {
		if (event.event === 'ping') return;
		let data: Record<string, unknown>;
		try {
			data = JSON.parse(event.data) as Record<string, unknown>;
		} catch {
			return;
		}
		switch (data.type) {
			case 'message_start': {
				const u = (data.message as { usage?: Record<string, number> } | undefined)?.usage;
				if (u) {
					usage.inputTokens = u.input_tokens ?? 0;
					usage.outputTokens = u.output_tokens ?? 0;
					usage.cacheReadTokens = u.cache_read_input_tokens ?? 0;
					usage.cacheWriteTokens = u.cache_creation_input_tokens ?? 0;
				}
				break;
			}
			case 'content_block_delta': {
				const d = data.delta as { type?: string; text?: string } | undefined;
				if (d?.type === 'text_delta' && typeof d.text === 'string') text += d.text;
				break;
			}
			case 'message_delta': {
				const d = data.delta as { stop_reason?: string | null } | undefined;
				if (d?.stop_reason) stopReason = d.stop_reason;
				const u = data.usage as Record<string, number> | undefined;
				if (u) {
					if (typeof u.output_tokens === 'number') usage.outputTokens = u.output_tokens;
					if (typeof u.input_tokens === 'number') usage.inputTokens = u.input_tokens;
					if (typeof u.cache_read_input_tokens === 'number')
						usage.cacheReadTokens = u.cache_read_input_tokens;
					if (typeof u.cache_creation_input_tokens === 'number')
						usage.cacheWriteTokens = u.cache_creation_input_tokens;
				}
				break;
			}
			case 'message_stop':
				ended = true;
				break;
			case 'error': {
				const type = (data.error as { type?: string } | undefined)?.type;
				const status =
					type === 'overloaded_error'
						? 529
						: type === 'rate_limit_error'
							? 429
							: type === 'api_error'
								? 500
								: 400;
				throw errorFrom(status, data, secrets);
			}
		}
	};

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		for (const ev of parser.push(decoder.decode(value, { stream: true }))) handle(ev);
	}
	for (const ev of parser.push(decoder.decode())) handle(ev);
	parser.end();
	if (!ended && stopReason === '') {
		throw new ProviderError({
			status: 0,
			type: 'network_error',
			message: 'the response stream ended early',
		});
	}
	return { text, stopReason: stopReason || 'end_turn', usage };
}

export function createAnthropicProvider(options: AnthropicOptions): Provider {
	const doFetch = options.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
	const url = options.url ?? API_URL;
	const maxRetries = options.maxRetries ?? 4;
	const maxTruncationRetries = options.maxTruncationRetries ?? 2;
	const base = options.baseDelayMs ?? 1000;
	const max = options.maxDelayMs ?? 60_000;
	const sleep = options.sleep ?? defaultSleep;
	const random = options.random ?? Math.random;
	const now = options.now ?? Date.now;
	const secrets = [options.apiKey];

	/** One HTTP request with retries for retryable failures. Returns the parsed stream. */
	async function attempt(req: CompletionRequest, maxTokens: number): Promise<StreamResult> {
		const body = JSON.stringify({
			...buildMessageParams(req, maxTokens, { cache: options.cache }),
			stream: true,
		});
		for (let n = 0; ; n++) {
			if (options.signal?.aborted)
				throw new ProviderError({ status: 0, type: 'cancelled', message: 'cancelled' });
			let retryAfter: number | null = null;
			let failure: ProviderError;
			try {
				const res = await doFetch(url, {
					method: 'POST',
					signal: options.signal,
					headers: {
						'content-type': 'application/json',
						'x-api-key': options.apiKey,
						'anthropic-version': API_VERSION,
						'anthropic-dangerous-direct-browser-access': 'true',
					},
					body,
				});
				if (options.onRateLimitHeaders) {
					const rem = res.headers.get('anthropic-ratelimit-output-tokens-remaining');
					const reset = res.headers.get('anthropic-ratelimit-output-tokens-reset');
					const remN = rem !== null && rem.trim() !== '' ? Number(rem) : Number.NaN;
					safely(options.onRateLimitHeaders, {
						outputTokensRemaining: Number.isFinite(remN) && remN >= 0 ? remN : null,
						outputTokensReset: reset !== null && reset.trim() !== '' ? reset.trim() : null,
					});
				}
				if (res.ok && res.body) return await readMessageStream(res.body, secrets);
				retryAfter = retryAfterMs(res.headers.get('retry-after'), now());
				let parsed: unknown = null;
				try {
					parsed = await res.json();
				} catch {
					// no JSON body
				}
				failure = errorFrom(res.status, parsed, secrets);
				if (retryAfter !== null)
					failure = new ProviderError({
						status: failure.status,
						type: failure.type,
						message: failure.message,
						retryAfterMs: retryAfter,
					});
			} catch (err) {
				if (err instanceof ProviderError) failure = err;
				else if (options.signal?.aborted)
					failure = new ProviderError({ status: 0, type: 'cancelled', message: 'cancelled' });
				else
					failure = new ProviderError({
						status: 0,
						type: 'network_error',
						message: 'network error',
					});
			}
			const retryable =
				failure.type !== 'cancelled' && (failure.status === 0 || RETRYABLE.has(failure.status));
			if (!retryable || n >= maxRetries) throw failure;
			if (failure.status === 429 || failure.status === 529)
				safely(options.onRateLimited, retryAfter);
			await sleep(backoffMs(n, retryAfter, { base, max, random }), options.signal);
		}
	}

	return {
		async complete(req: CompletionRequest): Promise<CompletionResponse> {
			if (!isAllowedModel(req.model)) {
				throw new ProviderError({
					status: 400,
					type: 'invalid_request_error',
					message: scrub(`model not allowed: ${req.model}`, secrets),
				});
			}
			let maxTokens = req.maxTokens;
			let usage: CompletionUsage | undefined;
			for (let t = 0; ; t++) {
				const r = await attempt(req, maxTokens);
				usage = addUsage(usage, r.usage);
				// A truncated output is retried with a larger limit; if it is still cut off at the ceiling, the caller sees max_tokens.
				if (
					req.schema === undefined &&
					r.stopReason === 'max_tokens' &&
					t < maxTruncationRetries &&
					maxTokens < MAX_TOKENS_CEILING
				) {
					maxTokens = Math.min(MAX_TOKENS_CEILING, Math.ceil(maxTokens * 1.5));
					continue;
				}
				const parsed = parsedOf(req, r.text);
				return {
					text: r.text,
					...(parsed !== undefined ? { parsed } : {}),
					stopReason: r.stopReason,
					usage,
				};
			}
		},
	};
}
